import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Loader2, Route, ScanLine, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

// Worker-facing claim form: type, receipt photo, amount (+tax), optional vendor/note/project/job/mileage.
export const SubmitClaimDialog = ({ open, onClose, policy, token, onSubmitted }) => {
  const [form, setForm] = useState({ category: '', amount: '', tax_amount: '', date: today(), vendor: '', note: '', project_id: '', job_id: '', mileage_km: '', immediate_requested: false });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const fileRef = useRef(null);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, category: f.category || policy?.categories?.[0] || '' }));
    axios.get(`${API}/projects`, h).then((r) => setProjects((Array.isArray(r.data) ? r.data : []).filter((p) => p.status !== 'closed'))).catch(() => {});
    axios.get(`${API}/jobs`, { ...h, params: { limit: 200 } }).then((r) => setJobs(Array.isArray(r.data) ? r.data : r.data?.jobs || [])).catch(() => {});
  }, [open]); // eslint-disable-line

  const pickFile = (f) => {
    setFile(f || null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    setScan(null);
    if (f && /^image\/(jpeg|jpg|png|webp)$/.test(f.type)) scanReceipt(f);
  };
  // Receipt Reader: prefill vendor/date/amount/tax/type from the photo; the worker just confirms.
  const scanReceipt = async (f) => {
    setScanning(true);
    try {
      const fd = new FormData(); fd.append('receipt', f, f.name);
      const r = await axios.post(`${API}/reimbursements/scan`, fd, { headers: { ...h.headers, 'Content-Type': 'multipart/form-data' } });
      const d = r.data;
      setForm((cur) => ({
        ...cur,
        vendor: d.vendor || cur.vendor,
        date: d.date || cur.date,
        amount: d.total != null ? String(d.total) : cur.amount,
        tax_amount: d.tax != null ? String(d.tax) : cur.tax_amount,
        category: d.category || cur.category,
      }));
      setScan({ ok: true, filled: ['vendor', 'date', 'total', 'tax', 'category'].filter((k) => d[k] != null && d[k] !== ''), confidence: d.confidence, notes: d.notes });
    } catch (e) {
      setScan({ ok: false, detail: e.response?.data?.detail || 'Could not read the receipt — enter the details manually.' });
    } finally { setScanning(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const km = parseFloat(form.mileage_km) || 0;
  const mileageAmt = km ? Math.round(km * (policy?.mileage_rate || 0) * 100) / 100 : 0;
  const amt = parseFloat(form.amount) || 0;
  const total = Math.round((amt + mileageAmt) * 100) / 100;

  const submit = async () => {
    setErr(null);
    if (!form.category) return setErr('Pick a reimbursement type.');
    if (!amt && !km) return setErr('Enter an amount or a distance.');
    if (policy?.require_receipt && amt > 0 && !file) return setErr('Add a photo of the receipt.');
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v === '' || v === null ? (k === 'immediate_requested' ? 'false' : k.endsWith('amount') || k === 'mileage_km' ? '0' : '') : String(v)));
    if (file) fd.append('receipt', file, file.name);
    setSaving(true);
    try {
      const r = await axios.post(`${API}/reimbursements`, fd, { headers: { ...h.headers, 'Content-Type': 'multipart/form-data' } });
      onSubmitted?.(r.data);
      setForm({ category: policy?.categories?.[0] || '', amount: '', tax_amount: '', date: today(), vendor: '', note: '', project_id: '', job_id: '', mileage_km: '', immediate_requested: false });
      pickFile(null);
      setScan(null);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not submit the claim.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="reimb-submit-dialog">
        <DialogHeader>
          <DialogTitle>Submit a reimbursement</DialogTitle>
          <DialogDescription>Only for purchases you were authorized to make for the company. An admin reviews every claim.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Type *</Label>
            <Select value={form.category} onValueChange={(v) => set('category', v)}>
              <SelectTrigger data-testid="reimb-category"><SelectValue placeholder="What is this for?" /></SelectTrigger>
              <SelectContent>{(policy?.categories || []).map((c) => <SelectItem key={c} value={c} data-testid={`reimb-category-${c.replace(/\W+/g, '-').toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Receipt photo {policy?.require_receipt ? '*' : '(optional)'}</Label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} data-testid="reimb-receipt-input" />
            {file ? (
              <div className="flex items-center gap-3 rounded-lg border border-[#1F2D3F] p-2" data-testid="reimb-receipt-preview">
                {preview ? <img src={preview} alt="Receipt" className="w-16 h-16 object-cover rounded" /> : <div className="w-16 h-16 rounded bg-[#0F1923] flex items-center justify-center text-xs text-slate-400">PDF</div>}
                <div className="min-w-0 flex-1 text-xs text-slate-300 truncate">{file.name}</div>
                <Button size="sm" variant="ghost" onClick={() => pickFile(null)} data-testid="reimb-receipt-remove"><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="w-full h-16 border-dashed" onClick={() => fileRef.current?.click()} data-testid="reimb-receipt-btn">
                <Camera className="w-5 h-5 mr-2" /> Take a photo / choose file
              </Button>
            )}
            {scanning && (
              <p className="text-xs text-sky-300 flex items-center gap-1" data-testid="reimb-scan-loading"><Loader2 className="w-3 h-3 animate-spin" /> Reading the receipt…</p>
            )}
            {!scanning && scan?.ok && (
              <p className="text-xs text-emerald-300 flex items-start gap-1" data-testid="reimb-scan-result">
                <ScanLine className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Read from the receipt: {scan.filled.length ? scan.filled.join(', ') : 'nothing legible'} — please check the values below{scan.notes ? ` (${scan.notes})` : ''}.</span>
              </p>
            )}
            {!scanning && scan && !scan.ok && (
              <p className="text-xs text-amber-400" data-testid="reimb-scan-error">{scan.detail}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1 col-span-1">
              <Label className="text-xs">Amount *</Label>
              <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="reimb-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tax included</Label>
              <Input type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={form.tax_amount} onChange={(e) => set('tax_amount', e.target.value)} data-testid="reimb-tax" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} max={today()} onChange={(e) => set('date', e.target.value)} data-testid="reimb-date" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Vendor / store</Label>
            <Input placeholder="e.g. Home Depot" value={form.vendor} onChange={(e) => set('vendor', e.target.value)} data-testid="reimb-vendor" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Project (optional)</Label>
              <Select value={form.project_id || '__none__'} onValueChange={(v) => set('project_id', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="reimb-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  <SelectItem value="__none__">None</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_number ? `${p.project_number} · ` : ''}{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Job (optional)</Label>
              <Select value={form.job_id || '__none__'} onValueChange={(v) => set('job_id', v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="reimb-job"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  <SelectItem value="__none__">None</SelectItem>
                  {jobs.slice(0, 200).map((j) => <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} · ` : ''}{j.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {policy?.mileage_enabled && (
            <div className="rounded-lg border border-[#1F2D3F] p-2 grid grid-cols-2 gap-2 items-end" data-testid="reimb-mileage">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1"><Route className="w-3 h-3" /> Mileage (km)</Label>
                <Input type="number" inputMode="decimal" min="0" step="0.1" placeholder="0" value={form.mileage_km} onChange={(e) => set('mileage_km', e.target.value)} data-testid="reimb-km" />
              </div>
              <p className="text-xs text-slate-400 pb-2">× ${Number(policy.mileage_rate || 0).toFixed(2)}/km = <b className="text-white" data-testid="reimb-mileage-amount">${mileageAmt.toFixed(2)}</b></p>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Note</Label>
            <Textarea rows={2} placeholder="Who authorized it, which site, anything accounting should know" value={form.note} onChange={(e) => set('note', e.target.value)} data-testid="reimb-note" />
          </div>

          {policy?.allow_immediate_request && (
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <Checkbox checked={form.immediate_requested} onCheckedChange={(v) => set('immediate_requested', !!v)} data-testid="reimb-immediate" />
              Request immediate payment (instead of waiting for the usual timing)
            </label>
          )}

          {err && <p className="text-xs text-rose-400" data-testid="reimb-error">{err}</p>}
        </div>
        <DialogFooter className="items-center gap-2">
          <span className="text-sm text-slate-300 mr-auto" data-testid="reimb-total">Total: <b className="text-white">${total.toFixed(2)}</b></span>
          <Button variant="outline" onClick={onClose} data-testid="reimb-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#1E88E5] hover:bg-blue-700" data-testid="reimb-submit">
            {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Submitting…</> : 'Submit claim'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
